/**
 * Vollebak feature-engagement tracking.
 *
 * A reusable tagging convention feeding the existing Elevar → GTM → GA4 pipeline:
 *   - container element:  data-engagement-source="<list key>"  (e.g. "search_recently_viewed")
 *   - each tile/item:     data-engagement-product='<JSON>'     (Elevar UA-style product fields)
 *
 * Behaviour:
 *   - First time a tagged container becomes visible → dl_view_item_list (impressions).
 *   - Click on a tagged item → dl_select_item with list attribution. Elevar's session
 *     enrichment then persists the `list` into its own dl_add_to_cart and server-side
 *     purchase events, so GA4 attributes ATC + revenue to the feature automatically.
 *
 * Event names/schema follow Elevar's spec (dl_ prefix — see the GTM trigger notes:
 * the prefix avoids clashes with the Google Shopping App). Verified end-to-end against
 * GTM-5HZ4ZT9 / G-X65F5CF84K on 2026-06-12.
 *
 * To track a new feature: add the two data attributes in its Liquid. Nothing else.
 */
(() => {
  if (window.__vbEngagementInit) return;
  window.__vbEngagementInit = true;

  /** @param {object} evt */
  const pushDl = (evt) => {
    window.ElevarDataLayer = window.ElevarDataLayer ?? [];
    window.ElevarDataLayer.push(evt);
  };

  const currencyCode = () =>
    (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';

  /**
   * @param {Element} el - element carrying data-engagement-product
   * @param {string} list - the engagement source key
   * @param {number} position - 1-based position within the container
   */
  const parseItem = (el, list, position) => {
    try {
      const data = JSON.parse(el.getAttribute('data-engagement-product') || '');
      return { ...data, list, position };
    } catch {
      return null;
    }
  };

  /* ---- First-party conversion attribution (independent of GA4/consent) ----
     Tagged interactions are journaled in localStorage and mirrored onto the cart as
     hidden cart attributes (the `_` prefix keeps them off customer-facing pages).
     Shopify carries cart attributes through checkout onto the ORDER, so attribution
     is readable in admin (Additional details), order CSV exports (Note Attributes
     column), the Admin API, and Shopify Flow conditions — no third party involved. */
  const TOUCH_KEY = 'vbEngagementTouches';
  const SYNC_KEY = 'vbEngagementSyncedTs'; // ts of the latest touch already mirrored to the cart
  const TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  const TOUCH_CAP = 15;

  const readTouches = () => {
    try {
      const map = JSON.parse(localStorage.getItem(TOUCH_KEY) || '{}');
      const cutoff = Date.now() - TOUCH_TTL_MS;
      for (const k of Object.keys(map)) if (!map[k] || map[k].ts < cutoff) delete map[k];
      return map;
    } catch {
      return {};
    }
  };

  const sortedEntries = () =>
    Object.entries(readTouches()).sort((a, b) => b[1].ts - a[1].ts).slice(0, TOUCH_CAP);

  /**
   * Mirror the current touches onto the cart as hidden attributes.
   * /cart/update.js MERGES only the keys provided, so other cart attributes (e.g. Elevar's)
   * are untouched. `_vb_attribution` maps product handles only (line-level revenue matching);
   * CTA touches surface via `_vb_last_touch`.
   *
   * CRITICAL: clicking a product tile navigates to the PDP, so this write must survive page
   * unload — hence `keepalive: true`. (The earlier debounced fetch was cancelled by navigation
   * every time, so zero attribution reached orders.) On success we record the synced ts so the
   * on-load reconciliation (syncCartFromTouches) doesn't re-write unnecessarily.
   */
  const mirrorToCart = (entries, lastTouch, keepalive) => {
    if (!entries.length) return;
    const attribution = {};
    for (const [h, t] of entries) if (!h.startsWith('cta:')) attribution[h] = t.list;
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive,
      body: JSON.stringify({
        attributes: { _vb_attribution: JSON.stringify(attribution), _vb_last_touch: lastTouch },
      }),
    })
      .then(() => {
        try {
          localStorage.setItem(SYNC_KEY, String(entries[0][1].ts));
        } catch {
          /* non-fatal */
        }
      })
      .catch(() => {});
  };

  /**
   * Journal a tagged interaction and mirror it to the cart.
   * @param {string} list - the engagement key
   * @param {string|null} handle - product handle (Tier 1) or null for CTA touches (Tier 2)
   */
  const recordTouch = (list, handle) => {
    const touches = readTouches();
    touches[handle || `cta:${list}`] = { list, ts: Date.now() }; // last-touch per product/CTA
    const entries = Object.entries(touches).sort((a, b) => b[1].ts - a[1].ts).slice(0, TOUCH_CAP);
    try {
      localStorage.setItem(TOUCH_KEY, JSON.stringify(Object.fromEntries(entries)));
    } catch {
      /* storage unavailable — non-fatal */
    }
    mirrorToCart(entries, list, true); // keepalive: survive the tile's navigation
  };

  /* On every page load, reconcile the cart with localStorage. The click-time keepalive write
     covers most cases; this catches anything it missed (browsers without keepalive, writes
     lost to navigation) — the localStorage journal always survives navigation, so the PDP the
     user lands on after a tile click re-mirrors reliably while the page is stable. */
  const syncCartFromTouches = () => {
    const entries = sortedEntries();
    if (!entries.length) return;
    const latestTs = String(entries[0][1].ts);
    let synced = '';
    try {
      synced = localStorage.getItem(SYNC_KEY) || '';
    } catch {
      /* non-fatal */
    }
    if (synced === latestTs) return; // cart already reflects the latest touch
    mirrorToCart(entries, entries[0][1].list, false);
  };
  syncCartFromTouches();

  /* A tagged click that also adds to cart (quick add, the sticky bar) sends its
     attribute write at the same moment as the cart add, and Shopify can apply
     the add on top of a cart read before the write, silently dropping
     _vb_last_touch (seen on Live 2026-10-19). The theme fires cart:update once
     an add has landed: shortly after, check the cart and write again if the
     latest touch is missing. Only within a minute of a touch, so ordinary cart
     edits cost nothing. */
  const RECHECK_WINDOW_MS = 60 * 1000;
  let recheckTimer = 0;
  const recheckCart = (attempt) => {
    const entries = sortedEntries();
    if (!entries.length || Date.now() - entries[0][1].ts > RECHECK_WINDOW_MS) return;
    const latest = entries[0][1].list;
    fetch('/cart.js', { headers: { Accept: 'application/json' } })
      .then((response) => response.json())
      .then((cart) => {
        if ((cart.attributes || {})._vb_last_touch === latest) return;
        mirrorToCart(entries, latest, false);
        if (attempt < 2) recheckTimer = window.setTimeout(() => recheckCart(attempt + 1), 1500);
      })
      .catch(() => {});
  };
  document.addEventListener('cart:update', () => {
    clearTimeout(recheckTimer);
    recheckTimer = window.setTimeout(() => recheckCart(1), 800);
  });

  /** Tagged items of a container; an element can be both the list and its only item. */
  const itemsIn = (container) =>
    container.matches('[data-engagement-product]')
      ? [container]
      : [...container.querySelectorAll('[data-engagement-product]')];

  /* ---- Impressions: dl_view_item_list, once per container reveal ---- */
  const seen = new WeakSet();
  const io = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const container = entry.target;
        io.unobserve(container);
        if (seen.has(container)) continue;
        seen.add(container);

        const list = container.getAttribute('data-engagement-source');
        const items = itemsIn(container)
          .map((el, i) => parseItem(el, list, i + 1))
          .filter(Boolean);
        if (!items.length) continue;

        pushDl({
          event: 'dl_view_item_list',
          ecommerce: { currencyCode: currencyCode(), impressions: items },
        });
      }
    },
    { threshold: 0.01 }
  );

  /* Containers can be (re)created at any time (section morphs, drawers, lazy sections):
     observe new ones as they appear. A container hidden at page load (e.g. inside the
     closed search modal) only fires once it actually becomes visible. */
  const scan = () => {
    document
      .querySelectorAll('[data-engagement-source]:not([data-engagement-bound])')
      .forEach((container) => {
        container.setAttribute('data-engagement-bound', '');
        io.observe(container);
      });
  };
  scan();
  new MutationObserver(scan).observe(document.documentElement, { childList: true, subtree: true });

  /* ---- Clicks: dl_select_item with list attribution ---- */
  document.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target.closest('[data-engagement-product]') : null;
      if (!target) return;
      const container = target.closest('[data-engagement-source]');
      if (!container) return;

      const list = container.getAttribute('data-engagement-source');
      const siblings = itemsIn(container);
      const item = parseItem(target, list, siblings.indexOf(target) + 1);
      if (!item) return;

      pushDl({
        event: 'dl_select_item',
        ecommerce: {
          currencyCode: currencyCode(),
          click: { actionField: { list, action: 'click' }, products: [item] },
        },
      });

      /* Feed Elevar's list-attribution store. Their add_to_cart/purchase events stamp items
         with the last list "touched", which they track via this storage key (it's what
         collection pages set). Writing our list key here makes Elevar attribute the
         subsequent ATC + purchase to this feature — verified on-device 2026-06-12.
         NB: internal Elevar key; if a future Elevar update renames it, events above still
         fire and attribution just falls back to Elevar's collection-based default. */
      try {
        localStorage.setItem('___ELEVAR_GTM_SUITE--lastCollectionPathname', list);
      } catch {
        /* storage unavailable — non-fatal */
      }

      /* First-party journal + cart-attribute mirror (independent of GA4/consent). */
      const handleMatch = typeof item.url === 'string' && item.url.match(/\/products\/([^/?#]+)/);
      if (handleMatch) recordTouch(list, handleMatch[1]);
      /* First-party click counter (VB Engagement dashboard) — handle as the
         "source" so repeat clicks on distinct products each count once. */
      sendEvent('product_click', list, handleMatch ? handleMatch[1] : '');
    },
    { capture: true, passive: true }
  );

  /* ---- Tier 2: CTA / non-product surfaces — data-engagement-cta="<key>" ----
     First-party order attribution only (no product payload needed). */
  document.addEventListener(
    'click',
    (event) => {
      const cta = event.target instanceof Element ? event.target.closest('[data-engagement-cta]') : null;
      if (!cta) return;
      const key = cta.getAttribute('data-engagement-cta');
      if (key) {
        recordTouch(key, null);
        // data-source (when present, e.g. the email block's Klaviyo source
        // label) identifies the PLACEMENT — the dashboard breaks counts
        // down by it and joins against Klaviyo's $source.
        sendEvent('cta_click', key, cta.getAttribute('data-source') || '');
      }
    },
    { capture: true, passive: true }
  );

  /* ---- First-party event counter (VB Engagement dashboard) ----
     Fire-and-forget beacon into a Google Forms endpoint whose responses
     land in a Workspace sheet (Workspace blocks anonymous web apps; it
     does NOT block anonymous form responses — verified 2026-07-21).
     Payload carries NO personal data: event name, feature key, path,
     device class (mobile / tablet / desktop).
     One beacon per (event,key) per page view — counts interactions, not
     nervous double-clicks. Public API for feature code:
       window.vbEngagement.track('email_submit', 'block_email_capture')  */
  const EVENTS_ENDPOINT =
    'https://docs.google.com/forms/d/e/1FAIpQLScN8COJdgQpQU6Q-Y73JqjxhSRV08hC5GVam6TWQezJBAPePQ/formResponse';
  const EVENTS_FIELD = 'entry.228449201'; // the ANSWER id from FB_PUBLIC_LOAD_DATA_, not the item id
  const sentEvents = new Set();

  /* Device class, matching Shopify's session_device_type (mobile / tablet /
     desktop) so dashboard splits line up with Shopify's own session reports.
     From the user agent only: no fingerprinting, nothing personal. */
  const deviceType = (() => {
    const ua = navigator.userAgent || '';
    if (/iPad|Tablet/i.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)) return 'tablet';
    if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return 'tablet';
    if (/Mobi|iPhone|iPod|Android/i.test(ua)) return 'mobile';
    return 'desktop';
  })();

  function sendEvent(eventName, key, source) {
    try {
      const dedupe = eventName + ':' + key + ':' + (source || '');
      if (sentEvents.has(dedupe)) return;
      sentEvents.add(dedupe);
      const payload = JSON.stringify({
        e: eventName,
        k: key,
        s: (source || '').slice(0, 64),
        p: location.pathname.slice(0, 128),
        d: deviceType,
      });
      const body = new URLSearchParams();
      body.set(EVENTS_FIELD, payload);
      if (navigator.sendBeacon) {
        navigator.sendBeacon(EVENTS_ENDPOINT, body);
      } else {
        fetch(EVENTS_ENDPOINT, { method: 'POST', mode: 'no-cors', body, keepalive: true });
      }
    } catch (e) {
      /* never let telemetry break the page */
    }
  }

  window.vbEngagement = window.vbEngagement || {};
  window.vbEngagement.track = sendEvent;
})();
